import React, { useEffect, useState, useCallback } from 'react';
import { Card, Button, Input, Table, Pagination, Badge, Modal } from '@/components/ui';
import type { Column, SortDirection } from '@/components/ui';
import { adminService } from '@/services/admin.service';
import type { AuditLog, AuditLogFilters } from '@/services/admin.service';

const ACTION_OPTIONS = [
  { value: '', label: 'All Actions' },
  { value: 'CREATE', label: 'Create' },
  { value: 'UPDATE', label: 'Update' },
  { value: 'DELETE', label: 'Delete' },
  { value: 'LOGIN', label: 'Login' },
  { value: 'LOGOUT', label: 'Logout' },
  { value: 'APPROVE', label: 'Approve' },
  { value: 'REJECT', label: 'Reject' },
  { value: 'ACTIVATE', label: 'Activate' },
  { value: 'DEACTIVATE', label: 'Deactivate' },
];

const TABLE_OPTIONS = [
  { value: '', label: 'All Tables' },
  { value: 'users', label: 'Users' },
  { value: 'tenants', label: 'Tenants' },
  { value: 'topup_requests', label: 'Topup Requests' },
  { value: 'wallets', label: 'Wallets' },
  { value: 'settings', label: 'Settings' },
];

const getActionBadgeVariant = (action: AuditLog['action']) => {
  switch (action) {
    case 'CREATE':
      return 'success';
    case 'UPDATE':
      return 'info';
    case 'DELETE':
      return 'danger';
    case 'LOGIN':
    case 'LOGOUT':
      return 'default';
    case 'APPROVE':
    case 'ACTIVATE':
      return 'success';
    case 'REJECT':
    case 'DEACTIVATE':
      return 'warning';
    default:
      return 'default';
  }
};

export const AuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [action, setAction] = useState('');
  const [tableName, setTableName] = useState('');
  const [userId, setUserId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 20;

  // Sorting
  const [sortColumn, setSortColumn] = useState<string>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Detail modal
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Export
  const [isExporting, setIsExporting] = useState(false);

  const fetchLogs = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const filters: AuditLogFilters = {
        page: currentPage,
        limit: itemsPerPage,
      };

      if (action) filters.action = action;
      if (tableName) filters.tableName = tableName;
      if (userId) filters.userId = userId;
      if (startDate) filters.startDate = startDate;
      if (endDate) filters.endDate = endDate;

      const response = await adminService.getAuditLogs(filters);
      setLogs(response.data);
      setTotalPages(response.meta.totalPages);
      setTotalItems(response.meta.total);
    } catch {
      setError('Failed to load audit logs');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, action, tableName, userId, startDate, endDate]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchLogs();
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const filters: AuditLogFilters = {};
      if (action) filters.action = action;
      if (tableName) filters.tableName = tableName;
      if (userId) filters.userId = userId;
      if (startDate) filters.startDate = startDate;
      if (endDate) filters.endDate = endDate;

      const blob = await adminService.exportAuditLogs(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      setError('Failed to export audit logs');
    } finally {
      setIsExporting(false);
    }
  };

  const clearFilters = () => {
    setAction('');
    setTableName('');
    setUserId('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  const hasActiveFilters = action || tableName || userId || startDate || endDate;

  const columns: Column<AuditLog>[] = [
    {
      key: 'createdAt',
      header: 'Timestamp',
      sortable: true,
      render: (log) => (
        <span className="text-sm text-secondary-700">
          {new Date(log.createdAt).toLocaleString()}
        </span>
      ),
    },
    {
      key: 'userName',
      header: 'User',
      sortable: true,
      render: (log) => (
        <div>
          <p className="text-sm font-medium text-secondary-900">{log.userName}</p>
          <p className="text-xs text-secondary-500">{log.userEmail}</p>
        </div>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      sortable: true,
      render: (log) => (
        <Badge variant={getActionBadgeVariant(log.action)}>{log.action}</Badge>
      ),
    },
    {
      key: 'tableName',
      header: 'Table',
      sortable: true,
      render: (log) => (
        <span className="text-sm text-secondary-700 capitalize">
          {log.tableName.replace(/_/g, ' ')}
        </span>
      ),
    },
    {
      key: 'recordId',
      header: 'Record ID',
      render: (log) => (
        <span className="text-sm font-mono text-secondary-500">
          {log.recordId.slice(0, 8)}...
        </span>
      ),
    },
    {
      key: 'changes',
      header: 'Changes',
      render: (log) => (
        <div className="max-w-xs">
          {log.newValues ? (
            <p className="text-xs text-secondary-500 truncate">
              {Object.keys(log.newValues).length} field(s) changed
            </p>
          ) : (
            <span className="text-xs text-secondary-400">-</span>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (log) => (
        <Button
          size="sm"
          variant="ghost"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedLog(log);
            setIsDetailModalOpen(true);
          }}
        >
          Details
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Audit Logs</h1>
          <p className="mt-1 text-sm text-secondary-500">
            View all system activity and changes ({totalItems} entries)
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="secondary"
            onClick={handleExport}
            isLoading={isExporting}
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            Export
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card padding="sm">
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <select
              value={action}
              onChange={(e) => {
                setAction(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2.5 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {ACTION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <select
              value={tableName}
              onChange={(e) => {
                setTableName(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2.5 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {TABLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <Input
              placeholder="User ID..."
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            />
            <Input
              type="date"
              placeholder="Start Date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <Input
              type="date"
              placeholder="End Date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="flex items-center justify-end space-x-3">
            {hasActiveFilters && (
              <Button type="button" variant="ghost" onClick={clearFilters}>
                Clear Filters
              </Button>
            )}
            <Button type="submit">Apply Filters</Button>
          </div>
        </form>
      </Card>

      {/* Error State */}
      {error && (
        <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <p className="text-danger-700">{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-danger-500 hover:text-danger-700"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <Table
        columns={columns}
        data={logs}
        keyExtractor={(log) => log.id}
        isLoading={isLoading}
        emptyMessage="No audit logs found"
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
        onRowClick={(log) => {
          setSelectedLog(log);
          setIsDetailModalOpen(true);
        }}
      />

      {/* Pagination */}
      {!isLoading && totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Detail Modal */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedLog(null);
        }}
        title="Audit Log Details"
        size="lg"
      >
        {selectedLog && (
          <>
            <Modal.Body>
              <div className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-secondary-500">Timestamp</dt>
                    <dd className="mt-1 text-sm text-secondary-900">
                      {new Date(selectedLog.createdAt).toLocaleString()}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-secondary-500">Action</dt>
                    <dd className="mt-1">
                      <Badge variant={getActionBadgeVariant(selectedLog.action)}>
                        {selectedLog.action}
                      </Badge>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-secondary-500">User</dt>
                    <dd className="mt-1 text-sm text-secondary-900">
                      {selectedLog.userName}
                      <br />
                      <span className="text-secondary-500">{selectedLog.userEmail}</span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-secondary-500">Table</dt>
                    <dd className="mt-1 text-sm text-secondary-900 capitalize">
                      {selectedLog.tableName.replace(/_/g, ' ')}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-secondary-500">Record ID</dt>
                    <dd className="mt-1 text-sm font-mono text-secondary-900">
                      {selectedLog.recordId}
                    </dd>
                  </div>
                  {selectedLog.ipAddress && (
                    <div>
                      <dt className="text-sm font-medium text-secondary-500">IP Address</dt>
                      <dd className="mt-1 text-sm font-mono text-secondary-900">
                        {selectedLog.ipAddress}
                      </dd>
                    </div>
                  )}
                </div>

                {/* Old Values */}
                {selectedLog.oldValues && Object.keys(selectedLog.oldValues).length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-secondary-700 mb-2">
                      Previous Values
                    </h4>
                    <div className="bg-danger-50 rounded-lg p-4 overflow-auto max-h-48">
                      <pre className="text-xs text-danger-700 whitespace-pre-wrap">
                        {JSON.stringify(selectedLog.oldValues, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

                {/* New Values */}
                {selectedLog.newValues && Object.keys(selectedLog.newValues).length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-secondary-700 mb-2">
                      New Values
                    </h4>
                    <div className="bg-success-50 rounded-lg p-4 overflow-auto max-h-48">
                      <pre className="text-xs text-success-700 whitespace-pre-wrap">
                        {JSON.stringify(selectedLog.newValues, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

                {/* User Agent */}
                {selectedLog.userAgent && (
                  <div>
                    <h4 className="text-sm font-medium text-secondary-700 mb-2">
                      User Agent
                    </h4>
                    <p className="text-xs text-secondary-500 bg-secondary-50 rounded-lg p-3">
                      {selectedLog.userAgent}
                    </p>
                  </div>
                )}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => {
                  setIsDetailModalOpen(false);
                  setSelectedLog(null);
                }}
              >
                Close
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </div>
  );
};

export default AuditLogs;
